import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import { getStudentEnrollments } from '../../services/enrollmentService';
import { MdSchedule, MdPerson, MdLocationOn } from 'react-icons/md';
import useAuth from '../../hooks/useAuth';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr   = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
};

const addCalendar = (cls) => {
  const today = new Date();
  const dayIdx = DAYS.indexOf(cls.days?.[0] || 'Monday');
  const diff   = (dayIdx - today.getDay() + 7) % 7;
  const date   = new Date(today);
  date.setDate(today.getDate() + diff);
  const [sh, sm] = (cls.startTime || '06:00').split(':').map(Number);
  const [eh, em] = (cls.endTime   || '07:00').split(':').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d, h, m) => `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(h)}${pad(m)}00`;
  const start = fmt(date, sh, sm);
  const end   = fmt(date, eh, em);
  const url   = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(cls.name)}&dates=${start}/${end}&recur=RRULE:FREQ=WEEKLY`;
  window.open(url, '_blank');
};

function ClassCard({ cls, isToday }) {
  return (
    <div className={`rounded-2xl border p-4 card-hover ${isToday ? 'border-primary bg-red-50' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-secondary text-sm">{cls.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">{cls.discipline}</p>
        </div>
        {isToday && <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full flex-shrink-0">Today</span>}
      </div>

      <div className="mt-3 space-y-1.5 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <MdSchedule className="text-primary flex-shrink-0" />
          <span>{fmtTime(cls.startTime)} – {fmtTime(cls.endTime)}</span>
        </div>
        {cls.trainer && (
          <div className="flex items-center gap-1.5">
            <MdPerson className="text-primary flex-shrink-0" />
            <span>{cls.trainer?.user?.name || cls.trainer?.fullName || 'TBA'}</span>
          </div>
        )}
        {cls.room && (
          <div className="flex items-center gap-1.5">
            <MdLocationOn className="text-primary flex-shrink-0" />
            <span>{cls.room}</span>
          </div>
        )}
      </div>

      <button
        onClick={() => addCalendar(cls)}
        className="mt-3 w-full text-xs text-primary border border-red-500/30 rounded-xl py-1.5 hover:bg-red-500/15 transition-colors"
      >
        + Add to Google Calendar
      </button>
    </div>
  );
}

function StudentSchedule() {
  document.title = 'Class Schedule — TrainAxis';
  const { user } = useAuth();

  const { data, isPending } = useQuery({
    queryKey: ['student-enrollments', user?.id],
    queryFn:  () => getStudentEnrollments(user.id).then(r => r.data),
    enabled:  !!user?.id,
  });

  const todayIdx  = new Date().getDay();
  const todayName = DAYS[todayIdx];

  // Extract class objects from enrollments
  const allClasses = (data?.enrollments || [])
    .map(e => e.classId)
    .filter(Boolean);

  const byDay = DAYS.reduce((acc, d) => {
    acc[d] = allClasses.filter(c => c.days?.includes(d));
    return acc;
  }, {});

  const upcomingDays = Array.from({ length: 7 }, (_, i) => {
    const idx  = (todayIdx + i) % 7;
    return { name: DAYS[idx], short: DAY_SHORT[idx], isToday: i === 0 };
  }).filter(d => byDay[d.name]?.length > 0);

  if (isPending) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-36 bg-gray-100 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }

  if (!allClasses.length) {
    return (
      <Card>
        <div className="flex flex-col items-center py-14 text-center">
          <MdSchedule className="text-5xl text-gray-200 mb-3" />
          <p className="text-gray-500 font-medium text-sm">You're not enrolled in any class yet</p>
          <p className="text-gray-400 text-xs mt-1">Contact your admin to get enrolled in a class</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-400 text-sm">{allClasses.length} class{allClasses.length !== 1 ? 'es' : ''} enrolled</p>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {DAYS.map((day, i) => {
          const count = byDay[day]?.length || 0;
          const isToday = i === todayIdx;
          return (
            <a
              key={day}
              href={`#day-${day}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                isToday
                  ? 'bg-primary text-white'
                  : count > 0
                  ? 'bg-gray-100 text-secondary hover:bg-gray-200'
                  : 'bg-gray-50 text-gray-300 cursor-default pointer-events-none'
              }`}
            >
              {DAY_SHORT[i]}
              {count > 0 && !isToday && <span className="ml-1 bg-white text-secondary rounded-full px-1">{count}</span>}
            </a>
          );
        })}
      </div>

      {upcomingDays.map(({ name, isToday }) => (
        <div key={name} id={`day-${name}`}>
          <h3 className={`text-sm font-semibold mb-3 ${isToday ? 'text-primary' : 'text-secondary'}`}>
            {isToday ? `Today · ${name}` : name}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {byDay[name].map(cls => (
              <ClassCard key={cls._id} cls={cls} isToday={isToday} />
            ))}
          </div>
        </div>
      ))}

      <Card>
        <h3 className="font-semibold text-secondary text-sm mb-4">Full Week Overview</h3>
        <div className="grid grid-cols-7 gap-1 text-center">
          {DAYS.map((day, i) => (
            <div key={day}>
              <p className={`text-xs font-semibold mb-2 ${i === todayIdx ? 'text-primary' : 'text-gray-400'}`}>
                {DAY_SHORT[i]}
              </p>
              {byDay[day].length === 0 ? (
                <div className="h-12 rounded-lg bg-gray-50" />
              ) : (
                byDay[day].map(cls => (
                  <div
                    key={cls._id}
                    className={`rounded-lg p-1 mb-1 text-xs ${i === todayIdx ? 'bg-red-100 text-primary' : 'bg-blue-50 text-blue-700'}`}
                  >
                    <p className="font-medium truncate">{cls.name}</p>
                    <p className="text-gray-400">{fmtTime(cls.startTime)}</p>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default StudentSchedule;
